import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Plus, Trash2, Edit2, ChevronLeft, ChevronRight, UserPlus, X, Phone, Mail,
  Shield, ShieldOff, KeyRound, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const PRESET_COLORS = [
  '#00D9CC', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6',
  '#EC4899', '#10B981', '#F97316', '#6366F1', '#14B8A6'
];

// ==================== STAFF LIST ====================
const StaffList = ({ staff, onUpdate }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState({});
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', role: 'estimator', color: '#00D9CC', calendar_email: '', is_active: true
  });

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', role: 'estimator', color: '#00D9CC', calendar_email: '', is_active: true });
    setEditingStaff(null);
  };

  const openEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      role: member.role,
      color: member.color,
      calendar_email: member.calendar_email || '',
      is_active: member.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }
    try {
      if (editingStaff) {
        await api.admin.updateStaff(editingStaff.id, formData);
        toast.success('Staff member updated');
      } else {
        await api.admin.createStaff(formData);
        toast.success('Staff member added');
      }
      setIsDialogOpen(false);
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff member');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    try {
      await api.admin.deleteStaff(id);
      toast.success('Staff member removed');
      onUpdate();
    } catch (error) {
      toast.error('Failed to remove staff member');
    }
  };

  const handleToggleAccess = async (member) => {
    setLoadingAccess(prev => ({ ...prev, [member.id]: true }));
    try {
      if (member.has_admin_access) {
        await api.admin.revokeStaffAccess(member.id);
        toast.success(`Admin access revoked for ${member.name}`);
      } else {
        await api.admin.grantStaffAccess(member.id);
        toast.success(`Admin access granted to ${member.name}. Invite email sent.`);
      }
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    } finally {
      setLoadingAccess(prev => ({ ...prev, [member.id]: false }));
    }
  };

  const handleResetPassword = async (member) => {
    if (!window.confirm(`Send password reset email to ${member.email}?`)) return;
    try {
      await api.admin.resetStaffPassword(member.id);
      toast.success(`Password reset email sent to ${member.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  return (
    <div data-testid="staff-list">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white">Team Members</h2>
        <Button
          onClick={() => { resetForm(); setIsDialogOpen(true); }}
          className="bg-electric-teal text-primary-dark font-bold"
          data-testid="add-staff-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {staff.map((member) => (
          <div
            key={member.id}
            className="bg-secondary-dark rounded-lg border border-white/10 p-5 relative"
            data-testid={`staff-card-${member.id}`}
          >
            {/* Color indicator */}
            <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-lg"
              style={{ backgroundColor: member.color }}
            />
            <div className="flex items-start justify-between pt-2">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: member.color }}
                >
                  {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                  <p className="text-white font-semibold">{member.name}</p>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${
                    member.role === 'admin' ? 'bg-electric-teal/20 text-electric-teal' : 
                    member.role === 'estimator' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {member.role}
                  </span>
                </div>
              </div>
              <div className="flex gap-1">
                <Button onClick={() => openEdit(member)} variant="ghost" size="sm" className="text-white/60 hover:text-white h-8 w-8 p-0">
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button onClick={() => handleDelete(member.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400 h-8 w-8 p-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Mail className="w-3.5 h-3.5" />
                <span>{member.email}</span>
              </div>
              {member.phone && (
                <div className="flex items-center gap-2 text-white/60 text-sm">
                  <Phone className="w-3.5 h-3.5" />
                  <span>{member.phone}</span>
                </div>
              )}
            </div>
            {!member.is_active && (
              <div className="mt-2 text-xs text-red-400 font-semibold uppercase">Inactive</div>
            )}

            {/* Admin Access Controls — Owner only */}
            {isOwner && (
              <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-xs">Admin Access</span>
                  <button
                    onClick={() => handleToggleAccess(member)}
                    disabled={loadingAccess[member.id]}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                      member.has_admin_access
                        ? 'bg-electric-teal/20 text-electric-teal border border-electric-teal/30'
                        : 'bg-white/5 text-white/30 border border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`access-toggle-${member.id}`}
                  >
                    {loadingAccess[member.id] ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : member.has_admin_access ? (
                      <><Shield className="w-3 h-3" /> Enabled</>
                    ) : (
                      <><ShieldOff className="w-3 h-3" /> Disabled</>
                    )}
                  </button>
                </div>
                {member.has_admin_access && (
                  <button
                    onClick={() => handleResetPassword(member)}
                    className="flex items-center gap-1.5 text-white/30 hover:text-white/60 text-xs transition-colors"
                    data-testid={`reset-password-${member.id}`}
                  >
                    <KeyRound className="w-3 h-3" /> Reset Password
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {staff.length === 0 && (
          <div className="col-span-full text-center py-12 text-white/40">
            No team members yet. Add your first estimator to get started.
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-secondary-dark border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editingStaff ? 'Edit' : 'Add'} Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white mb-2 block">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
                placeholder="John Smith"
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
                placeholder="john@islandcoastalelectric.ca"
                data-testid="staff-email-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
                placeholder="778.555.1234"
                data-testid="staff-phone-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white" data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimator">Estimator</SelectItem>
                  <SelectItem value="technician">Technician</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Calendar Color</Label>
              <div className="flex flex-wrap gap-2" data-testid="color-picker">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFormData({ ...formData, color: c })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      formData.color === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Calendar Email (for Google Calendar sync)</Label>
              <Input
                type="email"
                value={formData.calendar_email}
                onChange={(e) => setFormData({ ...formData, calendar_email: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
                placeholder="calendar@gmail.com"
                data-testid="staff-calendar-email-input"
              />
            </div>
            <Button onClick={handleSave} className="w-full bg-electric-teal text-primary-dark font-bold" data-testid="save-staff-btn">
              {editingStaff ? 'Update' : 'Add'} Team Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CALENDAR VIEW ====================
const getWeekDates = (baseDate) => {
  const start = new Date(baseDate);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Start on Monday
  start.setDate(start.getDate() + diff);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d);
  }
  return dates;
};

const formatDateStr = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const HOURS = Array.from({ length: 11 }, (_, i) => i + 7); // 7 AM to 5 PM

const CalendarView = ({ staff }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [staffList, setStaffList] = useState(staff || []);
  const [visibleStaff, setVisibleStaff] = useState(new Set());
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const startStr = formatDateStr(weekDates[0]);
  const endStr = formatDateStr(weekDates[6]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await api.admin.getCalendar(startStr, endStr);
      setBookings(res.data.bookings || []);
      if (res.data.staff) {
        setStaffList(res.data.staff);
        setVisibleStaff(prev => prev.size === 0 ? new Set(res.data.staff.map(s => s.id)) : prev);
      }
    } catch (error) {
      console.error('Failed to load calendar', error);
    }
  }, [startStr, endStr]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    if (staff) {
      setStaffList(staff);
      setVisibleStaff(new Set(staff.map(s => s.id)));
    }
  }, [staff]);

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction * 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const toggleStaffVisibility = (id) => {
    setVisibleStaff(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getBookingsForCell = (dateStr, hour) => {
    return bookings.filter(b => {
      if (b.preferred_date !== dateStr) return false;
      const [bh] = b.preferred_time.split(':').map(Number);
      if (bh !== hour) return false;
      const eid = b.assigned_estimator_id;
      if (eid && !visibleStaff.has(eid)) return false;
      if (!eid && visibleStaff.size < staffList.length) return true; // Show unassigned when some filters on
      return true;
    });
  };

  const isToday = (d) => {
    const t = new Date();
    return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
  };

  const handleDragStart = (e, booking) => {
    setDragItem(booking);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.id);
  };

  const handleDragOver = (e, dateStr, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(`${dateStr}-${hour}`);
  };

  const handleDragLeave = () => setDragOver(null);

  const handleDrop = async (e, dateStr, hour) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragItem) return;
    const newTime = `${String(hour).padStart(2, '0')}:00`;
    if (dragItem.preferred_date === dateStr && dragItem.preferred_time === newTime) { setDragItem(null); return; }
    try {
      await api.admin.rescheduleBooking(dragItem.id, dateStr, newTime, dragItem.assigned_estimator_id || '');
      toast.success(`Moved ${dragItem.customer_name} to ${dateStr} ${newTime}`);
      loadCalendar();
    } catch (err) { toast.error('Failed to reschedule'); }
    setDragItem(null);
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div data-testid="calendar-view">
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm" className="border-white/20 text-white h-8 w-8 p-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-lg font-bold text-white min-w-[200px] text-center">
            {monthNames[weekDates[0].getMonth()]} {weekDates[0].getDate()} - {monthNames[weekDates[6].getMonth()]} {weekDates[6].getDate()}, {weekDates[6].getFullYear()}
          </h2>
          <Button onClick={() => navigate(1)} variant="outline" size="sm" className="border-white/20 text-white h-8 w-8 p-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button onClick={goToToday} variant="outline" size="sm" className="border-white/20 text-white text-xs ml-2" data-testid="today-btn">
            Today
          </Button>
        </div>

        {/* Staff Legend / Filters */}
        <div className="flex flex-wrap gap-2" data-testid="staff-filters">
          {staffList.map(s => (
            <button
              key={s.id}
              onClick={() => toggleStaffVisibility(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                visibleStaff.has(s.id)
                  ? 'border-transparent text-white'
                  : 'border-white/20 text-white/30'
              }`}
              style={{ backgroundColor: visibleStaff.has(s.id) ? s.color + '33' : 'transparent' }}
              data-testid={`filter-${s.id}`}
            >
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: visibleStaff.has(s.id) ? s.color : '#666' }} />
              {s.name.split(' ')[0]}
            </button>
          ))}
          <button
            onClick={() => {
              if (visibleStaff.size === staffList.length) setVisibleStaff(new Set());
              else setVisibleStaff(new Set(staffList.map(s => s.id)));
            }}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-white/50 hover:text-white border border-white/10 hover:border-white/30 transition-all"
          >
            {visibleStaff.size === staffList.length ? 'Hide All' : 'Show All'}
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Day Headers */}
            <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-white/10">
              <div className="p-2" /> {/* Time column header */}
              {weekDates.map((d, i) => (
                <div
                  key={i}
                  className={`p-3 text-center border-l border-white/10 ${isToday(d) ? 'bg-electric-teal/10' : ''}`}
                >
                  <div className="text-white/50 text-xs font-medium uppercase">{dayNames[i]}</div>
                  <div className={`text-lg font-bold mt-0.5 ${isToday(d) ? 'text-electric-teal' : 'text-white'}`}>
                    {d.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Grid */}
            {HOURS.map((hour) => (
              <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-white/5 min-h-[56px]">
                {/* Time Label */}
                <div className="p-2 text-white/40 text-xs font-medium text-right pr-3 pt-1">
                  {hour > 12 ? hour - 12 : hour}{hour >= 12 ? 'PM' : 'AM'}
                </div>
                {/* Day Cells */}
                {weekDates.map((d, di) => {
                  const dateStr = formatDateStr(d);
                  const cellBookings = getBookingsForCell(dateStr, hour);
                  const isDragTarget = dragOver === `${dateStr}-${hour}`;
                  return (
                    <div
                      key={di}
                      className={`border-l border-white/10 p-0.5 transition-colors ${isToday(d) ? 'bg-electric-teal/5' : ''} ${
                        hour === 12 ? 'bg-white/[0.02]' : ''
                      } ${isDragTarget ? 'bg-electric-teal/20 ring-1 ring-electric-teal/50 ring-inset' : ''}`}
                      onDragOver={(e) => handleDragOver(e, dateStr, hour)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, dateStr, hour)}
                    >
                      {cellBookings.length > 0 && (
                        <div className="flex gap-0.5 h-full">
                          {cellBookings.map((b) => (
                            <div
                              key={b.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, b)}
                              className="flex-1 rounded px-1.5 py-1 text-xs cursor-grab active:cursor-grabbing overflow-hidden group relative hover:ring-1 hover:ring-white/30"
                              style={{
                                backgroundColor: (b.estimator_color || '#666') + '33',
                                borderLeft: `3px solid ${b.estimator_color || '#666'}`
                              }}
                              title={`${b.customer_name} - ${b.service_type}${b.estimator_name ? ' (' + b.estimator_name + ')' : ''} (drag to reschedule)`}
                              data-testid={`booking-${b.id}`}
                            >
                              <div className="font-semibold text-white truncate">{b.customer_name}</div>
                              <div className="text-white/60 truncate">{b.service_type}</div>
                              {b.estimator_name && (
                                <div className="text-white/40 truncate text-[10px]">{b.estimator_name}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== COMBINED STAFF TAB ====================
const StaffCalendarTab = ({ staff: initialStaff, onUpdate }) => {
  const [staff, setStaff] = useState(initialStaff || []);
  const [view, setView] = useState('team'); // 'team' or 'calendar'

  useEffect(() => {
    setStaff(initialStaff || []);
  }, [initialStaff]);

  const handleUpdate = () => {
    onUpdate();
  };

  return (
    <div className="space-y-6" data-testid="staff-calendar-tab">
      {/* View Switcher */}
      <div className="flex gap-2 bg-secondary-dark rounded-lg p-1 w-fit border border-white/10">
        <button
          onClick={() => setView('team')}
          className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
            view === 'team' ? 'bg-electric-teal text-primary-dark' : 'text-white/60 hover:text-white'
          }`}
          data-testid="view-team-btn"
        >
          Team Members
        </button>
        <button
          onClick={() => setView('calendar')}
          className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
            view === 'calendar' ? 'bg-electric-teal text-primary-dark' : 'text-white/60 hover:text-white'
          }`}
          data-testid="view-calendar-btn"
        >
          Schedule Calendar
        </button>
      </div>

      {view === 'team' ? (
        <StaffList staff={staff} onUpdate={handleUpdate} />
      ) : (
        <CalendarView staff={staff} />
      )}
    </div>
  );
};

export default StaffCalendarTab;
